import { useState, useEffect } from "react";
import { API_BASE_URL, API_ENDPOINTS } from "../../../config/apiConfig";
import axiosInstance from "../../../config/axiosConfig";
import "./DocumentDialog.css";

export const DocumentDialog = ({ document, isOpen, onClose }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [vcoFiles, setVcoFiles] = useState(null);
  const [vcoFilesDownloadingIds, setVcoFilesDownloadingIds] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  // MOD: loading flag for VCO files
  const [isLoadingVcoFiles, setIsLoadingVcoFiles] = useState(false);

  // Auto-cancella l'errore dopo 5 secondi
  useEffect(() => {
    if (downloadError) {
      const timer = setTimeout(() => {
        setDownloadError(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [downloadError]);

  useEffect(() => {
    if (!document) return;

    // MOD: condition VCO fetch only when id_email exists (and it's a VCO email doc)
    const idEmail = document?.documento_originale?.id_email;
    const isVcoEmail = document?.fonte_indice === "vco-emails";

    if (!isVcoEmail || !idEmail) {
      setVcoFiles(null);
      setVcoFilesDownloadingIds(null);
      setIsLoadingVcoFiles(false);
      return;
    }

    fetchVcoFiles(idEmail);
  }, [document]);

  // MOD: accept idEmail as param, encode, manage loading
  const fetchVcoFiles = async (idEmail) => {
    setVcoFiles(null);
    setVcoFilesDownloadingIds(null);
    setIsLoadingVcoFiles(true);

    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}${API_ENDPOINTS.VCO}/files?id_email=${encodeURIComponent(
          idEmail
        )}`
      );

      setVcoFiles(response.data);
      setVcoFilesDownloadingIds(
        Object.fromEntries(
          (response.data?.files ?? []).map((file) => [String(file.id), false])
        )
      );
    } catch (err) {
    } finally {
      setIsLoadingVcoFiles(false);
    }
  };

  if (!isOpen || !document) return null;

  const handleDownloadPDF = async () => {
    const idEvento = document.documento_originale?.id_evento;
    const nomeDocumentoDB = document.documento_originale?.nome_documento;

    if (!idEvento) {
      setDownloadError("ID evento non disponibile");
      return;
    }

    setIsDownloading(true);
    setDownloadError(null);

    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}${API_ENDPOINTS.EVENTI_PDF}?id_evento=${idEvento}`,
        {
          responseType: "blob",
        }
      );

      // Usa il nome dal database come priorità
      let filename = nomeDocumentoDB || `evento_${idEvento}.pdf`;

      // Se manca l'estensione .pdf, aggiungila
      if (!filename.toLowerCase().endsWith(".pdf")) {
        filename = `${filename}.pdf`;
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Errore durante il download del PDF:", error);
      setDownloadError(`Errore durante il download: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadExcel = async () => {
    const fileId = document.documento_originale?.file_id;
    const nomeDocumentoDB = document.documento_originale?.oggetto;

    if (!fileId) {
      setDownloadError("ID evento non disponibile");
      return;
    }

    setIsDownloading(true);
    setDownloadError(null);

    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}${API_ENDPOINTS.REGIONALI_XLSX}?id_file=${fileId}`,
        {
          responseType: "blob",
        }
      );

      // Usa il nome dal database come priorità
      let filename = nomeDocumentoDB || `regionale_${fileId}.xlsx`;

      // Se manca l'estensione .xlsx, aggiungila
      if (!filename.toLowerCase().endsWith(".xlsx")) {
        filename = `${filename}.xlsx`;
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Errore durante il download del Excel:", error);
      setDownloadError(`Errore durante il download: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileDownload = async (e, file) => {
    e.preventDefault();

    if (!vcoFiles) return;

    const idFile = file.id;
    const idEmail = vcoFiles.email_id;

    setVcoFilesDownloadingIds((prev) => ({ ...prev, [file.id]: true }));

    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}${API_ENDPOINTS.VCO}/single_file?id_file=${idFile}&id_email=${idEmail}`,
        { responseType: "blob" }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = file.filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log("Errore durante il download del file:", error);
      setDownloadError(`Errore durante il download: ${error.message}`);
    } finally {
      setVcoFilesDownloadingIds((prev) => ({ ...prev, [file.id]: false }));
    }
  };

  const formatDocumentContent = (doc) => {
    const original = doc.documento_originale;
    let content = `# ${original.oggetto || "Documento"}\n\n`;

    if (original.provvedimenti) {
      content += `## Provvedimenti\n\n${original.provvedimenti}\n\n`;
    }

    if (original.testo) {
      content += `## Testo\n\n${original.testo}\n\n`;
    }

    if (original.provvedimento) {
      content += `## Provvedimento Regionale\n\n**Treno:** ${original.treno}\n\n**Tratta:** ${original.tratta}\n\n**(Ora partenza/arrivo):** ${original.orario}\n\n**Provvedimento:** ${original.provvedimento}\n\n**Note:** ${original.note || ''}\n\n`;
    }

    if (original.contenuto_email) {
      content += `## Contenuto\n\n ${original.contenuto_email} \n\n`;
    }

    if (original.id_infomobilita) {
      const data = original.data.split("T")[0];

      content += `## Infomobilità\n\n**${original.titolo}**\n\n**Descrizione:**\n\n${original.description}\n\n**\n\n${data}\n\n`;
    }

    content += `**Punteggio ricerca:** ${doc.punteggio_ricerca.toFixed(2)}\n`;
    content += `**Fonte:** ${doc.fonte_indice}`;

    return content;
  };

  const parseMarkdownForDocument = (text) => {
    const parseTable = (tableText) => {
      const lines = tableText.trim().split("\n");
      if (lines.length < 2) return tableText;

      const separatorLine = lines[1];
      if (!separatorLine.includes("-")) return tableText;

      const headers = lines[0]
        .split("|")
        .map((h) => h.trim())
        .filter((h) => h);
      if (headers.length === 0) return tableText;

      const dataRows = [];
      for (let i = 2; i < lines.length; i++) {
        const row = lines[i]
          .split("|")
          .map((cell) => cell.trim())
          .filter((cell) => cell);
        if (row.length > 0) dataRows.push(row);
      }

      const headerHtml = headers.map((h) => `<th>${h}</th>`).join("");
      const rowsHtml = dataRows
        .map(
          (row) =>
            `<tr>${row
              .slice(0, headers.length)
              .map((cell) => `<td title="${cell}">${cell}</td>`)
              .join("")}</tr>`
        )
        .join("");

      return `<div class="table-container"><table class="markdown-table">
        <thead><tr>${headerHtml}</tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table></div>`;
    };

    text = text.replace(
      /(?:^|\n)(\|[^\n]+\|\n\|[-:\s|]+\|\n(?:\|[^\n]+\|\n?)*)/gm,
      (match, table) => {
        return parseTable(table);
      }
    );

    text = text.replace(/^### (.*$)/gim, "<h3>$1</h3>");
    text = text.replace(/^## (.*$)/gim, "<h2>$1</h2>");
    text = text.replace(/^# (.*$)/gim, "<h1>$1</h1>");
    text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    text = text.replace(/\*(.*?)\*/g, "<em>$1</em>");
    text = text.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    text = text.replace(/`([^`]+)`/g, "<code>$1</code>");
    text = text.replace(/^[\s]*-\s+(.*)$/gim, "<li>$1</li>");
    text = text.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");
    text = text.replace(/\n/g, "<br/>");

    return text;
  };

  const formattedContent = parseMarkdownForDocument(
    formatDocumentContent(document)
  );
  const isEvento = document.fonte_indice === "evento-provvedimenti";
  const isRegionale = document.fonte_indice === "provvedimenti-regionali";

  // MOD: local flags for rendering condition
  const isVcoEmail = document?.fonte_indice === "vco-emails";
  const idEmail = document?.documento_originale?.id_email;

  const nomeDocumento =
    document.documento_originale?.nome_documento ||
    document.documento_originale?.oggetto + ".xlsx";

  return (
    <div className="document-overlay" onClick={onClose}>
      <div className="document-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="document-header">
          <h3>📄 Documento Sorgente</h3>
          <button className="document-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="document-content">
          <div dangerouslySetInnerHTML={{ __html: formattedContent }} />

          {/* MOD: render VCO section only when it makes sense */}
          {isVcoEmail && idEmail && (
            <>
              <h2>Variazioni in Corso d'Orario</h2>

              {isLoadingVcoFiles && (
                <>
                  <span className="loading-spinner"></span>
                  Caricamento...
                </>
              )}

              {!isLoadingVcoFiles &&
                vcoFiles?.files?.map((file) => {
                  const key = String(file.id);
                  const downloading = !!vcoFilesDownloadingIds?.[key];

                  return (
                    <div className="event-container" key={key}>
                      <strong>📥</strong>
                      <span
                        className={`pdf-download-link ${
                          downloading ? "downloading" : ""
                        }`}
                        onClick={(e) => handleFileDownload(e, file)}
                      >
                        {downloading ? "⏳ Download..." : file.filename}
                      </span>
                    </div>
                  );
                })}
            </>
          )}

          {/* Mostra il file SOLO per eventi */}
          {isEvento && nomeDocumento && (
            <div className="event-container">
              <strong>📥</strong>
              <span
                className={`pdf-download-link ${
                  isDownloading ? "downloading" : ""
                }`}
                onClick={handleDownloadPDF}
                onMouseEnter={(e) => {
                  if (!isDownloading) {
                    e.target.style.background =
                      "linear-gradient(135deg, rgba(45, 90, 74, 0.25) 0%, rgba(31, 63, 51, 0.25) 100%)";
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow =
                      "0 2px 8px rgba(45, 90, 74, 0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background =
                    "linear-gradient(135deg, rgba(45, 90, 74, 0.15) 0%, rgba(31, 63, 51, 0.15) 100%)";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                {isDownloading ? "⏳ Download..." : nomeDocumento}
              </span>
            </div>
          )}

          {isRegionale && nomeDocumento && (
            <div className="event-container">
              <strong>📥</strong>
              <span
                className={`pdf-download-link ${
                  isDownloading ? "downloading" : ""
                }`}
                onClick={handleDownloadExcel}
                onMouseEnter={(e) => {
                  if (!isDownloading) {
                    e.target.style.background =
                      "linear-gradient(135deg, rgba(45, 90, 74, 0.25) 0%, rgba(31, 63, 51, 0.25) 100%)";
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow =
                      "0 2px 8px rgba(45, 90, 74, 0.3)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background =
                    "linear-gradient(135deg, rgba(45, 90, 74, 0.15) 0%, rgba(31, 63, 51, 0.15) 100%)";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                }}
              >
                {isDownloading ? "⏳ Download..." : nomeDocumento}
              </span>
            </div>
          )}

          {downloadError && (
            <div className="download-error">⚠️ {downloadError}</div>
          )}
        </div>
      </div>
    </div>
  );
};
